import { useState } from 'react';
import { ChevronLeft, ChevronRight, Camera, Expand } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { SwipeableZoomImage, useSwipeNav } from '@/components/ui/swipeable-zoom-image';

interface PropertyImageGalleryProps {
  images: any[];
  title: string;
  isFeatured?: boolean;
}

export function PropertyImageGallery({ images, title, isFeatured }: PropertyImageGalleryProps) {
  const [current, setCurrent] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  const fallback = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&h=600&fit=crop';
  const urls: string[] = images.map((img: any) => img?.url || fallback);
  const mainImage = urls[current] || fallback;
  const imageCount = images.length;

  const prev = () => setCurrent(i => (i - 1 + imageCount) % imageCount);
  const next = () => setCurrent(i => (i + 1) % imageCount);
  const swipeHandlers = useSwipeNav(prev, next);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-[1.6fr_1fr] gap-1 rounded-xl overflow-hidden bg-muted">
        {/* Main image */}
        <div
          className="relative aspect-[16/10] md:aspect-auto md:h-[460px] overflow-hidden cursor-pointer group touch-pan-y"
          onClick={() => setLightbox(true)}
          {...swipeHandlers}
        >
          <img src={mainImage} alt={title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />

          {isFeatured && (
            <Badge className="absolute top-3 left-3 bg-destructive hover:bg-destructive text-destructive-foreground text-xs font-bold uppercase tracking-wider px-3 py-1">
              Super Hot
            </Badge>
          )}

          {imageCount > 1 && (
            <>
              <Button variant="secondary" size="icon" className="absolute left-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/90 shadow-md opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => { e.stopPropagation(); prev(); }}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button variant="secondary" size="icon" className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/90 shadow-md opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => { e.stopPropagation(); next(); }}>
                <ChevronRight className="h-5 w-5" />
              </Button>
            </>
          )}

          <div className="absolute bottom-3 left-3 flex items-center gap-1.5 bg-black/70 text-white text-xs px-2.5 py-1 rounded">
            <Camera className="h-3.5 w-3.5" />{imageCount} Photos
          </div>

          <button
            className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-white text-foreground text-xs font-semibold px-3.5 py-2 shadow-lg hover:bg-white/95 transition-colors"
            onClick={(e) => { e.stopPropagation(); setLightbox(true); }}
          >
            <Expand className="h-3.5 w-3.5" />
            See all {imageCount} photos
          </button>
        </div>

        {/* 2x2 side thumbnails (desktop) — Zillow-style hero */}
        {imageCount > 1 && (
          <div className="hidden md:grid grid-cols-2 grid-rows-2 gap-1 h-[460px]">
            {images.slice(1, 5).map((img: any, i: number) => {
              const idx = i + 1;
              const isLastWithMore = i === 3 && imageCount > 5;
              return (
                <button
                  key={img.id || i}
                  onClick={() => isLastWithMore ? setLightbox(true) : setCurrent(idx)}
                  className={`relative overflow-hidden ${idx === current ? 'ring-2 ring-primary' : 'opacity-90 hover:opacity-100'} transition-all`}
                >
                  <img src={img.url} alt="" className="w-full h-full object-cover" />
                  {isLastWithMore && (
                    <div className="absolute inset-0 bg-black/55 flex items-center justify-center text-white font-bold">
                      <span className="flex items-center gap-1.5 text-sm">
                        <Expand className="h-4 w-4" />
                        +{imageCount - 5} more
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Lightbox */}
      <Dialog open={lightbox} onOpenChange={setLightbox}>
        <DialogContent className="max-w-5xl p-0 bg-black border-none">
          <div className="relative h-[85vh]">
            <SwipeableZoomImage
              images={urls}
              index={current}
              onIndexChange={setCurrent}
              alt={title}
            />
            {imageCount > 1 && (
              <>
                <Button variant="secondary" size="icon" className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 z-20" onClick={prev}>
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <Button variant="secondary" size="icon" className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 z-20" onClick={next}>
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </>
            )}
            <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-black/70 text-white text-sm px-3 py-1 rounded z-20">
              {current + 1} / {imageCount}
            </div>
          </div>
          {/* Thumbnail strip */}
          <div className="flex gap-1 p-2 overflow-x-auto bg-black">
            {images.map((img: any, i: number) => (
              <button key={img.id || i} onClick={() => setCurrent(i)}
                className={`w-16 h-12 shrink-0 rounded overflow-hidden border-2 transition-all ${i === current ? 'border-primary' : 'border-transparent opacity-60 hover:opacity-100'}`}>
                <img src={img.url} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
